import { tokens } from '@neo4j-ndl/base';
import { DataGrid, Typography } from '@neo4j-ndl/react';
import { ChevronRightIconOutline } from '@neo4j-ndl/react/icons';
import { type FleetManagementTypes } from '@nx/state';
import { DataGridHelpers } from '@nx/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useState } from 'react';

import { useColumns } from '../dbms-details/hooks/use-columns';

type DeploymentServersProps = {
  dbms: FleetManagementTypes.DbmsDetails;
};

const ServerDataGrid = ({ details }: { details: FleetManagementTypes.Server[] }) => {
  const columns = useColumns('SERVERS', false);

  const table = useReactTable({
    data: details,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    columns: columns as ColumnDef<FleetManagementTypes.Server>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    columnResizeMode: 'onChange',
    enableSorting: true,
  });

  return (
    <DataGridHelpers.Wrapper>
      <DataGrid
        tableInstance={table}
        components={{
          RowsPerPage: DataGridHelpers.RowsPerPageWithBodyMenuPortal,
        }}
        styling={{
          borderStyle: 'horizontal',
          headerStyle: 'clean',
        }}
        isKeyboardNavigable={false}
        isAutoResizingColumns={true}
      />
    </DataGridHelpers.Wrapper>
  );
};

export const DeploymentServers = ({ dbms }: DeploymentServersProps) => {
  const [showServers, setShowServers] = useState(false);

  const handleToggleServers = () => {
    setShowServers(!showServers);
  };

  return (
    <div aria-label={`Servers for ${dbms.name}`} className="relative flex flex-col gap-4 p-4">
      <div className="flex w-full justify-between">
        <div
          role="button"
          tabIndex={0}
          aria-label="Toggle Servers"
          aria-pressed={showServers}
          onKeyDown={(e) => e.key === 'Enter' && handleToggleServers()}
          className="text-palette-neutral-text-weak flex cursor-pointer items-center gap-2"
          onClick={handleToggleServers}
        >
          <ChevronRightIconOutline
            aria-label="Show Servers"
            className="size-4"
            style={{
              transform: showServers ? 'rotate(90deg)' : 'rotate(0deg)',
              transitionDuration: tokens.transitions.values.duration.quick,
            }}
          />
          {/* add margin right to balance tab outline */}
          <Typography variant="subheading-small" className="mr-2">
            Servers ({dbms.servers.length})
          </Typography>
        </div>
      </div>
      {showServers && <ServerDataGrid details={dbms.servers} />}
    </div>
  );
};
